import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import type {
  MascotSlot,
  SceneKey,
  ThemeManifest,
  ThemePack,
  ThemeRegistry,
} from '@classy/theme-sdk';

export interface ThemeContextValue {
  registry: ThemeRegistry;
  pack: ThemePack;
  manifest: ThemeManifest;
  /** All registered themes — used by the picker. */
  available: ThemeManifest[];
  isThemeAllowed: (key: string) => boolean;
  setThemeKey: (key: string) => void;
  getBackground: (scene: SceneKey) => string;
  getMascot: (slot: MascotSlot) => string;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

/**
 * Like useTheme(), but returns null when no ThemeProvider is in scope.
 * For components that use the theme purely for cosmetic enhancement
 * (e.g. the classroom-card mascot preview) so they render fine in
 * test fixtures and pre-theme contexts.
 */
export function useThemeOptional(): ThemeContextValue | null {
  return useContext(ThemeContext);
}

export interface ThemeProviderProps {
  registry: ThemeRegistry;
  /** The desired theme key (often persisted via /api/preferences). */
  themeKey: string;
  /** Fallback when `themeKey` is unknown to the registry. */
  fallbackKey?: string;
  /** Called when the user picks a different theme. */
  onChange?: (key: string) => void;
  /** School-level allowlist. Omit to expose every registered theme. */
  allowedThemeKeys?: readonly string[] | undefined;
  /** When true, the theme's `tokens` overrides (accent, cardBg,
   *  textPrimary) are applied globally on `document.documentElement`
   *  so elements OUTSIDE this subtree (e.g. MmPageNav rendered at App
   *  level) also pick up the override. Wrappers around the full live
   *  experience (SessionBg / ClassroomBg / ClassroomLandingBg) pass
   *  `true`; small preview wrappers (mascot card thumbs) leave it off
   *  so they don't fight the active live theme. */
  setGlobalAccent?: boolean;
  children: ReactNode;
}

export function ThemeProvider({
  registry,
  themeKey,
  fallbackKey = 'jungle',
  onChange,
  allowedThemeKeys,
  setGlobalAccent = false,
  children,
}: ThemeProviderProps) {
  const [localKey, setLocalKey] = useState(themeKey);

  // Sync prop changes (e.g. parent's TanStack Query refetch lands a new
  // themeKey) into local state. User clicks update localKey directly.
  useEffect(() => {
    setLocalKey(themeKey);
  }, [themeKey]);

  const effectiveKey = localKey;
  const allowedSet = useMemo(
    () => (allowedThemeKeys ? new Set(allowedThemeKeys) : null),
    [allowedThemeKeys],
  );
  const isThemeAllowed = useCallback(
    (key: string) => !allowedSet || allowedSet.has(key),
    [allowedSet],
  );

  const available = useMemo(
    () => registry.list().filter((theme) => isThemeAllowed(theme.key)),
    [registry, isThemeAllowed],
  );

  const pack: ThemePack = useMemo(() => {
    const found =
      (isThemeAllowed(effectiveKey) ? registry.get(effectiveKey) : undefined) ??
      (isThemeAllowed(fallbackKey) ? registry.get(fallbackKey) : undefined) ??
      (available[0] ? registry.get(available[0].key) : undefined);
    if (!found) {
      const list = registry.list().map((m) => m.key);
      throw new Error(
        `[ThemeProvider] Neither "${effectiveKey}" nor fallback "${fallbackKey}" found in registry. Available: ${list.join(', ')}`,
      );
    }
    return found;
  }, [registry, effectiveKey, fallbackKey, isThemeAllowed, available]);

  const setThemeKey = useCallback(
    (next: string) => {
      if (!isThemeAllowed(next) || !registry.get(next)) return;
      setLocalKey(next);
      onChange?.(next);
    },
    [registry, isThemeAllowed, onChange],
  );

  const value: ThemeContextValue = useMemo(
    () => ({
      registry,
      pack,
      manifest: pack.manifest,
      available,
      isThemeAllowed,
      setThemeKey,
      getBackground: (scene) => pack.getBackground(scene),
      getMascot: (slot) => pack.getMascot(slot),
    }),
    [registry, pack, available, isThemeAllowed, setThemeKey],
  );

  // Apply per-theme palette overrides (if the manifest declared any)
  // by setting `--token-*` custom properties on a wrapper div. The
  // wrapper uses `display: contents` so it's invisible to layout — CSS
  // variables still cascade to descendants. Themes that don't declare
  // a given token fall through to the design-token default in
  // tokens.css. Anything added here must also be allowed by the
  // manifest schema (packages/theme-sdk/src/manifest.ts).
  const tokenOverrides = useMemo<ReadonlyArray<readonly [string, string]>>(() => {
    const t = pack.manifest.tokens;
    if (!t) return [];
    const entries: Array<readonly [string, string]> = [];
    if (t.accent) entries.push(['--token-accent', t.accent]);
    if (t.cardBg) entries.push(['--token-card-bg', t.cardBg]);
    if (t.textPrimary) entries.push(['--token-text-primary', t.textPrimary]);
    return entries;
  }, [pack.manifest.tokens]);

  const themeStyle = useMemo<CSSProperties>(() => {
    const style: Record<string, string> = {};
    for (const [k, v] of tokenOverrides) style[k] = v;
    return style as CSSProperties;
  }, [tokenOverrides]);

  // For elements rendered OUTSIDE this subtree (e.g. position-fixed
  // chrome like MmPageNav at App level), mirror the overrides onto
  // document.documentElement when opted-in. Restore prior values on
  // unmount / when the overrides clear so other ThemeProviders don't
  // inherit stale values.
  useEffect(() => {
    if (!setGlobalAccent || typeof document === 'undefined') return;
    const el = document.documentElement;
    const previous = new Map<string, string>();
    const ALL_KEYS = ['--token-accent', '--token-card-bg', '--token-text-primary'] as const;
    for (const k of ALL_KEYS) previous.set(k, el.style.getPropertyValue(k));
    for (const [k, v] of tokenOverrides) el.style.setProperty(k, v);
    // Clear any previously-set keys that this theme doesn't override.
    const setKeys = new Set(tokenOverrides.map(([k]) => k));
    for (const k of ALL_KEYS) if (!setKeys.has(k)) el.style.removeProperty(k);
    return () => {
      for (const k of ALL_KEYS) {
        const prev = previous.get(k);
        if (prev) el.style.setProperty(k, prev);
        else el.style.removeProperty(k);
      }
    };
  }, [setGlobalAccent, tokenOverrides]);

  return (
    <ThemeContext.Provider value={value}>
      <div style={{ display: 'contents', ...themeStyle }}>{children}</div>
    </ThemeContext.Provider>
  );
}

export const ThemeContextProvider = ThemeContext.Provider;
